import { useInfiniteQuery } from 'react-query';
import type { QueryOptions } from 'react-query';
import { IProduct, searchClient } from 'apis/algolia';

import type { SearchResponse } from '@algolia/client-search';

import { CATEGORY_SORT_VALUES } from 'constants/categories';

type FacetFilters =
  | ReadonlyArray<string>
  | ReadonlyArray<ReadonlyArray<string> | string>;
type NumericFilters =
  | ReadonlyArray<string>
  | ReadonlyArray<ReadonlyArray<string>>;

type CommonArgs = {
  hits_per_page?: number;
  // per algolia documentation the queryKey accepts an array of `unknown` type
  query_key: string | Array<unknown>;
  query_params: Array<{
    facet_filters: FacetFilters;
    numeric_filters: NumericFilters;
  }>;
  search_query?: string;
  sort_by?: string;
};

const ALGOLIA_VENDOR_LIST_INDEX = 'vendors_list_v1';
const ALGOLIA_VENDOR_LIST_INDEX_SORTED = 'vendors_list_v1_created_desc';
const ALGOLIA_VENDOR_LIST_PRICE_ASC = 'vendors_list_v1_usd_special_price_asc';
const ALGOLIA_VENDOR_LIST_PRICE_DESC = 'vendors_list_v1_usd_special_price_desc';
const PRODUCT_LIMIT_PER_PAGE = 9;
const PRODUCT_LIMIT_PER_PAGE_SORTED = 12;

const getSortIndex = (sort_by = '') => {
  switch (sort_by) {
    case CATEGORY_SORT_VALUES.BEST_SELLERS:
      return ALGOLIA_VENDOR_LIST_INDEX;
    case CATEGORY_SORT_VALUES.NEW_ARRIVALS:
      return ALGOLIA_VENDOR_LIST_INDEX_SORTED;
    case CATEGORY_SORT_VALUES.PRICE_ASC:
      return ALGOLIA_VENDOR_LIST_PRICE_ASC;
    case CATEGORY_SORT_VALUES.PRICE_DESC:
      return ALGOLIA_VENDOR_LIST_PRICE_DESC;
    default:
      return ALGOLIA_VENDOR_LIST_INDEX_SORTED;
  }
};

const groupAndCountResultsForMultipleWallets = <T = unknown>(
  results: Array<SearchResponse<T>>
) =>
  results.reduce<{ items: Array<T>; count: number; pages: number }>(
    (acc, result) => {
      acc.items.push(...result.hits);
      acc.count += result.nbHits;
      acc.pages = Math.max(acc.pages, result.nbPages);
      return acc;
    },
    { items: [], count: 0, pages: 0 }
  );

const getProducts = async ({
  page,
  ...common_args
}: {
  page: number;
} & CommonArgs) => {
  const { search_query, sort_by } = common_args;

  const indexName = getSortIndex(sort_by);
  const product_limit = sort_by
    ? PRODUCT_LIMIT_PER_PAGE_SORTED
    : PRODUCT_LIMIT_PER_PAGE;

  const queries = common_args.query_params.map((params) => ({
    indexName,
    query: search_query ?? '',
    params: {
      page,
      hitsPerPage: common_args.hits_per_page ?? product_limit,
      facetFilters: params.facet_filters,
      numericFilters: params.numeric_filters,
    },
  }));
  const { results } = await searchClient.multipleQueries<IProduct>(queries);

  const { count, items, pages } =
    groupAndCountResultsForMultipleWallets(results);

  return {
    max_pages: pages,
    // for the sorted results we truncate the products at the limit
    products: sort_by ? items.slice(0, PRODUCT_LIMIT_PER_PAGE_SORTED) : items,
    total_products: count,
  };
};

const getNextPageParam: QueryOptions<
  Awaited<ReturnType<typeof getProducts>>
>['getNextPageParam'] = (last_page, pages) =>
  pages.length !== 0 && pages.length < last_page.max_pages
    ? pages.length
    : undefined;

const useInfiniteQueryAlgoliaProducts = ({
  onError,
  enabled = true,
  ...common_args
}: {
  onError: (error: Error) => void;
  enabled?: boolean;
} & CommonArgs) => {
  const {
    data,
    fetchNextPage,
    hasNextPage: has_next_page,
    isFetchingNextPage: is_fetching_next_page,
    isLoading: is_loading,
  } = useInfiniteQuery(
    common_args.query_key,
    ({ pageParam: page = 0 }) =>
      getProducts({
        page,
        ...common_args,
      }),
    {
      getNextPageParam,
      onError,
      enabled,
    }
  );

  return {
    data,
    is_loading,
    is_fetching_next_page,
    fetchNextPage,
    has_next_page,
  };
};

export default useInfiniteQueryAlgoliaProducts;
